import { useParams, Link } from 'react-router-dom';
import { categoryArticles, categoryMeta } from '@/mocks/categoryData';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';

const categoryTabs = [
  { slug: 'pingce', label: '平台评测' },
  { slug: 'youhui', label: '优惠活动' },
  { slug: 'peilv', label: '赔率分析' },
  { slug: 'anquan', label: '安全指南' },
  { slug: 'zixun', label: '行业资讯' },
  { slug: 'celve', label: '策略技巧' },
];

export default function CategoryPage() {
  const { slug = 'pingce' } = useParams<{ slug: string }>();
  const meta = categoryMeta[slug];
  const articles = categoryArticles[slug] || [];

  if (!meta) {
    return (
      <>
        <Navbar />
        <div className="pt-20 min-h-screen flex items-center justify-center">
          <p className="text-foreground-600">分类不存在</p>
        </div>
        <Footer />
      </>
    );
  }

  const schema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'CollectionPage',
        name: `${meta.name} - 博彩网`,
        url: `https://bo-cai-wang.com/category/${slug}`,
        description: meta.description,
        isPartOf: { '@id': 'https://bo-cai-wang.com/#website' },
        inLanguage: 'zh-CN',
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://bo-cai-wang.com' },
          { '@type': 'ListItem', position: 2, name: meta.name, item: `https://bo-cai-wang.com/category/${slug}` },
        ],
      },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema, null, 2) }} />
      <Navbar />

      <main className="pt-20">
        {/* Breadcrumb */}
        <div className="px-4 md:px-6 py-4 bg-background-100/50 border-b border-background-200/70">
          <div className="max-w-7xl mx-auto flex items-center gap-2 text-sm text-foreground-500">
            <Link to="/" className="hover:text-accent-600 transition-colors">首页</Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-foreground-900 font-medium">{meta.name}</span>
          </div>
        </div>

        {/* Category Header */}
        <section className="py-10 md:py-14 px-4 md:px-6 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600">
                <i className={`${meta.icon} text-xl`}></i>
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900">
                  {meta.name}
                </h1>
              </div>
            </div>
            <p className="text-sm md:text-base text-foreground-600 max-w-2xl mt-2 pl-16">
              {meta.description}
            </p>
          </div>
        </section>

        {/* Category Tabs */}
        <div className="sticky top-16 z-40 bg-background-50 border-b border-background-200/70">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-7xl mx-auto flex items-center gap-1 py-3 overflow-x-auto">
              {categoryTabs.map((tab) => (
                <Link
                  key={tab.slug}
                  to={`/category/${tab.slug}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors duration-200 ${
                    slug === tab.slug
                      ? 'bg-accent-500 text-background-50'
                      : 'text-foreground-600 hover:text-foreground-900 hover:bg-background-100'
                  }`}
                >
                  {tab.label}
                </Link>
              ))}
            </div>
          </div>
        </div>

        {/* Articles Grid */}
        <section className="py-10 md:py-14 px-4 md:px-6 bg-background-100/70">
          <div className="max-w-7xl mx-auto">
            {articles.length === 0 ? (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-200">
                  <i className="ri-file-list-3-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-600">暂无相关文章</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {articles.map((article) => (
                  <Link
                    key={article.slug}
                    to={`/article/${article.slug}`}
                    className="group bg-background-50 border border-background-200/70 rounded-lg overflow-hidden hover:border-accent-300/50 transition-all duration-300"
                  >
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={article.cover}
                        alt={article.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      {article.featured && (
                        <div className="absolute top-3 left-3">
                          <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-accent-500/90 text-background-50 text-xs font-medium">
                            精选
                          </span>
                        </div>
                      )}
                    </div>

                    <div className="p-5">
                      <h3 className="text-base font-semibold text-foreground-900 mb-2 line-clamp-2 leading-snug group-hover:text-accent-600 transition-colors duration-200">
                        {article.title}
                      </h3>
                      <p className="text-sm text-foreground-600 leading-relaxed mb-4 line-clamp-2">
                        {article.excerpt}
                      </p>

                      <div className="flex items-center justify-between text-xs text-foreground-500">
                        <div className="flex items-center gap-3">
                          <span className="flex items-center gap-1">
                            <i className="ri-calendar-line"></i>
                            {article.date}
                          </span>
                          <span className="flex items-center gap-1">
                            <i className="ri-time-line"></i>
                            {article.readTime}
                          </span>
                        </div>
                        <span className="flex items-center gap-1 text-accent-600 font-medium">
                          阅读 <i className="ri-arrow-right-s-line"></i>
                        </span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}